import { HoverCard } from "#components/ui/HoverCard.tsx";
import { CitationPill, type CitationPillTone } from "#features/conversations/components/conversation-source/CitationPill.tsx";
import { SourcePreviewCard } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewCard.tsx";
import { SourcePreviewFooterLinkContext } from "#features/conversations/components/conversation-source/source-preview/SourcePreviewFooterLinkContext.ts";
import { useHoverCard } from "#hooks/useHoverCard.ts";
import type { ConversationSource } from "#types/types.ts";
import { Link, TextContext } from "@hopper-ui/components";
import { useId, useMemo, useRef, type MouseEventHandler } from "react";
import "./CitedSourceLink.css";

// The tooltip's type size, which the previews' Hopper Text was drawn at.
const CARD_TEXT_CONTEXT = { size: "xs" } as const;

interface Props {
    source: ConversationSource;
    /** The number the pill shows. */
    index: number;
    tone?: CitationPillTone;
    ariaLabel: string;
    isHovered: boolean;
    disabled?: boolean;
    onClick?: MouseEventHandler;
    onMouseEnter: () => void;
    onMouseLeave: () => void;
}

/** Canonical visual renderer for a numbered inline citation and its source card. */
export const CitedSourceLink = ({
    source,
    index,
    tone,
    ariaLabel,
    isHovered,
    disabled,
    onClick,
    onMouseEnter,
    onMouseLeave
}: Props) => {
    const hasValidUrl = Boolean(source.url);
    const triggerRef = useRef<HTMLAnchorElement>(null);
    const cardRef = useRef<HTMLElement>(null);
    const cardId = useId();
    const { isOpen, triggerProps, popoverProps, cardProps } = useHoverCard({ triggerRef, cardRef, isDisabled: disabled });
    const footerLink = useMemo(() => ({ onClick }), [onClick]);

    return (
        <>
            <Link
                ref={triggerRef}
                href={hasValidUrl ? source.url! : undefined}
                isExternal={hasValidUrl}
                textDecoration="none"
                display="inline-flex"
                className="wai-cited-source-link"
                aria-label={ariaLabel}
                aria-disabled={!hasValidUrl}
                aria-describedby={isOpen ? cardId : undefined}
                onClick={onClick}
                onMouseEnter={onMouseEnter}
                onMouseLeave={onMouseLeave}
                cursor={hasValidUrl ? "pointer" : "default"}
                {...triggerProps}
            >
                <CitationPill source={source} displayNumber={index} isActive={isHovered} tone={tone} />
            </Link>
            <HoverCard triggerRef={triggerRef} cardRef={cardRef} cardId={cardId} label={source.title} className="wai-source-hover-card" popoverProps={popoverProps} cardProps={cardProps}>
                <TextContext.Provider value={CARD_TEXT_CONTEXT}>
                    <SourcePreviewFooterLinkContext.Provider value={footerLink}>
                        <SourcePreviewCard source={source} />
                    </SourcePreviewFooterLinkContext.Provider>
                </TextContext.Provider>
            </HoverCard>
        </>
    );
};
